<template>
  <c-box
    v-chakra="{
      '& .return-type-card': {
        borderRadius: 'base',
        bg: 'white',
        maxWidth: '44rem',
        py: { base: 'xl', sm: 'xl' },
        pl: { base: 'md', sm: 'xl', lg: '3rem' },
        pr: { base: 'xs', sm: 'xl', lg: '3rem' },
        boxShadow: 'base',
        mb: 'xl',
        '&.disabled': {
          bg: 'gray.100',
        },
        '&.active': {
          bg: 'brand.blueLightest',
          boxShadow: '0px 0px 10px 1px rgba(0, 0, 0, 0.1)',
        },
      },
    }"
  >
    <c-heading as="h3" size="sm" :my="{ base: 'md', md: '3xl' }">
      {{ $t("productReturnV2.typeSelection.subtitle") }}
    </c-heading>
    <c-box
      class="return-type-card"
      :class="`enabled ${formTypeIsMaintenanceReturn() ? 'active' : ''}`"
      d="flex"
      cursor="pointer"
      @click="doReturn(ReturnType.MAINTENANCE_RETURN)"
      @keydown.enter="doReturn(ReturnType.MAINTENANCE_RETURN)"
      tabindex="0"
      data-cy="maintenance-return-card"
    >
      <c-box>
        <c-heading as="h4" size="lg" font-weight="bold" mb="xs">
          {{ $t("productReturn.typeSelection.maintenanceReturn.title") }}
        </c-heading>
        <c-text font-size="md">
          {{ $t("productReturn.typeSelection.maintenanceReturn.subtitle") }}
        </c-text>

        <c-box>
          <c-text mt="xs" font-size="sm" text-decoration="underline">
            <c-link
              :href="maintenanceReturnPolicyUrl"
              target="_blank"
              @click="(e) => e.stopPropagation()"
              :d="{ base: 'block', sm: 'inline' }"
              mr="xl"
            >
              {{ $t("productReturnV2.returnPolicyLinkTitle") }}
            </c-link>
            <c-link
              :href="repairServicePriceListUrl"
              target="_blank"
              @click="(e) => e.stopPropagation()"
              :d="{ base: 'block', sm: 'inline' }"
            >
              {{ $t("productReturnV2.repairServicePriceList") }}
            </c-link>
            <c-tooltip
              has-arrow
              placement="bottom"
              p="sm"
              border-radius="input"
              :label="$t('productReturnV2.repairServicePriceList.tooltip')"
              @click="
                (e) => {
                  if (e) {
                    e.stopPropagation();
                  }
                }
              "
            >
              <c-text as="span" tabindex="0">
                <c-icon
                  name="info"
                  color="brand.blue"
                  size="1.4rem"
                  data-cy="repair-service-pirce-list-info-icon"
                />
              </c-text>
            </c-tooltip>
          </c-text>
        </c-box>
      </c-box>
      <c-flex ml="auto" align-items="center">
        <c-icon name="chevron_right" size="1.4rem" />
      </c-flex>
    </c-box>
    <c-box
      v-if="hasOrdersApplicableForSalesReturn()"
      :class="`return-type-card ${formTypeIsSalesReturn() ? 'active' : ''}`"
      d="flex"
      cursor="pointer"
      @click="doReturn(ReturnType.SALES_RETURN)"
      @keydown.enter="doReturn(ReturnType.SALES_RETURN)"
      tabindex="0"
      data-cy="sales-return-card"
    >
      <c-box>
        <c-heading as="h4" size="lg" font-weight="bold" mb="xs">
          {{ $t("productReturn.typeSelection.salesReturn.title") }}
        </c-heading>
        <c-text font-size="md">
          {{ $t("productReturn.typeSelection.salesReturn.subtitle") }}
        </c-text>
        <c-text mt="xs" font-size="sm" text-decoration="underline">
          <c-link
            :href="salesReturnPolicyUrl"
            target="_blank"
            @click="(e) => e.stopPropagation()"
            mr="xl"
          >
            {{ $t("productReturnV2.returnPolicyLinkTitle") }}
          </c-link>
        </c-text>
      </c-box>
      <c-flex ml="auto" align-items="center">
        <c-icon name="chevron_right" size="1.4rem" />
      </c-flex>
    </c-box>
    <c-flex v-else class="return-type-card disabled">
      <c-box>
        <c-heading as="h4" size="lg" font-weight="bold" mb="xs">
          {{ $t("productReturn.typeSelection.salesReturn.title") }}
        </c-heading>
        <c-text font-size="md">
          {{ $t("productReturn.typeSelection.salesReturn.noOrders") }}
        </c-text>

        <c-text mt="xs" font-size="sm" text-decoration="underline">
          <c-link
            :href="salesReturnPolicyUrl"
            target="_blank"
            @click="(e) => e.stopPropagation()"
            mr="xl"
          >
            {{ $t("productReturnV2.returnPolicyLinkTitle") }}
          </c-link>
        </c-text>
      </c-box>
    </c-flex>

    <c-box
      v-if="showCampaignReturn"
      d="flex"
      :class="`return-type-card ${formTypeIsCampaignReturn() ? 'active' : ''}`"
      cursor="pointer"
      @click="doReturn(ReturnType.CAMPAIGN_RETURN)"
      @keydown.enter="doReturn(ReturnType.CAMPAIGN_RETURN)"
      tabindex="0"
      data-cy="campaign-return-card"
    >
      <c-box>
        <c-heading as="h4" size="lg" font-weight="bold" mb="xs">
          {{ $t("productReturn.typeSelection.campaignReturn.title") }}
        </c-heading>
        <c-text font-size="md">
          {{ $t("productReturn.typeSelection.campaignReturn.subtitle") }}
        </c-text>
      </c-box>
      <c-flex ml="auto" align-items="center">
        <c-icon name="chevron_right" size="1.4rem" />
      </c-flex>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { Logger } from "@iloq/shared";
import {
  campaignReturnBase,
  CorePermission,
  maintenanceReturnBase,
  ReturnType,
  salesReturnBase,
  IRootState,
} from "@iloq/shared";
import config from "@/config";
import { filterOrders } from "@/util/OrdersApplicableForSalesReturnFilter";
import Heading from "@/design-system/elements/Heading.vue";
import Icon from "@/design-system/elements/Icon.vue";
import { formMixin } from "./RMAMixins";
import { CBox, CFlex, CHeading, CIcon, CLink, CText, CTooltip } from "@chakra-ui/vue";
import { getRepairServicePriceListUrl } from "@/util/PriceListUtil";

export default defineComponent({
  name: "RMATypeSelection",
  mixins: [formMixin],
  components: {
    CTooltip,
    Heading,
    Icon,
    CBox,
    CIcon,
    CText,
    CHeading,
    CLink,
    CFlex,
  },
  computed: {
    maintenanceReturnPolicyUrl(): string {
      return config.get("maintenanceReturnPolicyUrl");
    },
    salesReturnPolicyUrl(): string {
      return config.get("salesReturnPolicyUrl");
    },
    ...mapState({
      partnerInfo: (state: unknown) => (state as IRootState).partnerInfo.partnerInfo,
      user: (state: unknown) => (state as IRootState).user!,
      returnListing: (state: unknown) => (state as IRootState).returns?.list,
      ordersApplicableForSalesReturn: (state: unknown) =>
        (state as IRootState).orders.ordersApplicableForSalesReturns,
    }),
    showCampaignReturn(): boolean {
      const { country } = this.partnerInfo;
      const campaignReturnAllowedCountryList: string[] = config.get(
        "campaignReturnAllowedCountryList",
      );

      return campaignReturnAllowedCountryList.includes(country);
    },
    repairServicePriceListUrl(): string {
      return getRepairServicePriceListUrl(this.user);
    },
  },
  data() {
    return {
      ReturnType: ReturnType,
    };
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  methods: {
    doReturn(returnType: ReturnType): void {
      switch (returnType) {
        case ReturnType.MAINTENANCE_RETURN:
          this.$emit("onSelect", maintenanceReturnBase);
          break;
        case ReturnType.SALES_RETURN:
          this.$emit("onSelect", salesReturnBase);
          break;
        case ReturnType.CAMPAIGN_RETURN:
          this.$emit("onSelect", campaignReturnBase);
          break;
        default:
          Logger.warn(`Trying to do return with unsupported type: ${returnType}`);
      }
    },
    hasOrderPermissions(): boolean {
      return this.user.hasPermissions([CorePermission.order]);
    },
    hasOrdersApplicableForSalesReturn(): boolean {
      if (this.hasOrderPermissions()) {
        return (
          filterOrders(this.ordersApplicableForSalesReturn, this.returnListing ?? []).length > 0
        );
      }
      return false;
    },
  },
});
</script>
